<script setup>
/* global axios */
// Página de detalhes do card (rota própria) — fiel ao detalhe do AstraChat original:
// header + Sequências + Contato + abas (Tarefas funcional; demais dependem de módulos).
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const accountId = route.params.accountId;
const cardId = route.params.cardId;
const base = `/api/v1/accounts/${accountId}/kanban`;

const card = ref(null);
const loading = ref(true);
const activeTab = ref('tarefas');
const newTaskTitle = ref('');
const newTaskDue = ref('');

const conv = computed(() => card.value?.conversation || null);

// Carregamento principal + dependências (definidos cedo p/ uso em ações abaixo)
const cfValues = ref({});
const seqExecs = ref([]);
const seqFlows = ref([]);
const syncCfValues = () => {
  const map = {};
  (card.value?.custom_fields || []).forEach(f => {
    const v = (card.value?.custom_field_values || []).find(
      x => x.field_id === f.id
    );
    if (f.field_type === 'multi_select') {
      try {
        map[f.id] = v?.value ? JSON.parse(v.value) : [];
      } catch (e) {
        map[f.id] = [];
      }
    } else if (f.field_type === 'checkbox') {
      map[f.id] = v?.value === 'true';
    } else {
      map[f.id] = v?.value || '';
    }
  });
  cfValues.value = map;
};
const loadSequences = async () => {
  if (!conv.value) return;
  try {
    seqExecs.value =
      (
        await axios.get(`${base}/flow_executions`, {
          params: { conversation_id: conv.value.id },
        })
      ).data.data || [];
  } catch (e) {
    seqExecs.value = [];
  }
  try {
    seqFlows.value = (
      (await axios.get(`${base}/flows`)).data.data || []
    ).filter(f => f.flow_type === 'sequence' && f.is_active);
  } catch (e) {
    seqFlows.value = [];
  }
};
const load = async () => {
  card.value = (await axios.get(`${base}/cards/${cardId}`)).data.data;
  syncCfValues();
  loadSequences();
  loading.value = false;
};

// Vincular conversa a um card avulso já criado
const showLinkConv = ref(false);
const linkQuery = ref('');
const linkResults = ref([]);
const linkLoading = ref(false);
const searchLinkConvs = async () => {
  linkLoading.value = true;
  try {
    linkResults.value =
      (
        await axios.get(`${base}/conversations`, {
          params: { q: linkQuery.value },
        })
      ).data.data || [];
  } finally {
    linkLoading.value = false;
  }
};
const openLinkConv = () => {
  showLinkConv.value = true;
  linkQuery.value = '';
  linkResults.value = [];
  searchLinkConvs();
};
const linkConversation = async cv => {
  await axios.patch(`${base}/cards/${cardId}/link_conversation`, {
    conversation_id: cv.id,
  });
  showLinkConv.value = false;
  await load();
};

const title = computed(
  () =>
    card.value?.custom_name || conv.value?.contact_name || `#${card.value?.id}`
);
const subtitle = computed(() =>
  conv.value
    ? conv.value.inbox_name || t('KANBAN.detail.conversation')
    : t('KANBAN.detail.standaloneCard')
);
const leadLabel = s =>
  ({
    won: t('KANBAN.card.leadWonLabel'),
    lost: t('KANBAN.card.leadLostLabel'),
  })[s] || t('KANBAN.card.leadOpenLabel');
const leadCls = s =>
  ({
    won: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
    lost: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  })[s] ||
  'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300';
const tasks = computed(() => card.value?.tasks || []);
const items = computed(() => card.value?.items || []);
const templates = computed(() => card.value?.item_templates || []);
const fmtMoney = cents =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
    (cents || 0) / 100
  );
const itemsTotal = computed(() =>
  items.value.reduce((s, i) => s + i.value * i.quantity, 0)
);
const showAddItem = ref(false);
const addMode = ref('template');
const selTemplate = ref(null);
const itForm = ref({
  title: '',
  description: '',
  valueReais: '0,00',
  quantity: 1,
});
const reaisToCents = v =>
  Math.round(
    (parseFloat(String(v).replace(/\./g, '').replace(',', '.')) || 0) * 100
  );
const openAddItem = () => {
  showAddItem.value = true;
  addMode.value = templates.value.length ? 'template' : 'manual';
  selTemplate.value = null;
  itForm.value = {
    title: '',
    description: '',
    valueReais: '0,00',
    quantity: 1,
  };
};
const pickTemplate = tpl => {
  selTemplate.value = tpl;
  itForm.value = {
    title: tpl.title,
    description: tpl.description || '',
    valueReais: ((tpl.value || 0) / 100).toFixed(2).replace('.', ','),
    quantity: 1,
  };
};
const saveItem = async () => {
  const payload = {
    title: itForm.value.title.trim(),
    description: itForm.value.description,
    value: reaisToCents(itForm.value.valueReais),
    quantity: Number(itForm.value.quantity) || 1,
  };
  if (!payload.title) return;
  if (addMode.value === 'template' && selTemplate.value)
    payload.template_id = selTemplate.value.id;
  await axios.post(`${base}/cards/${cardId}/items`, payload);
  showAddItem.value = false;
  await load();
};
const removeItem = async i => {
  await axios.delete(`${base}/cards/${cardId}/items/${i.id}`);
  await load();
};
const attachments = computed(() => card.value?.attachments || []);
const fileInput = ref(null);
const uploading = ref(false);
const fmtSize = b => {
  if (b < 1024) return `${b} B`;
  if (b < 1048576) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1048576).toFixed(1)} MB`;
};
const pickFile = () => fileInput.value?.click();
const uploadFile = async e => {
  const f = e.target.files?.[0];
  if (!f) return;
  uploading.value = true;
  try {
    const fd = new FormData();
    fd.append('file', f);
    await axios.post(`${base}/cards/${cardId}/attachments`, fd);
    await load();
  } finally {
    uploading.value = false;
    if (fileInput.value) fileInput.value.value = '';
  }
};
const removeAttachment = async a => {
  await axios.delete(`${base}/cards/${cardId}/attachments/${a.id}`);
  await load();
};
const scheduled = computed(() => card.value?.scheduled_messages || []);
const pendingScheduled = computed(
  () => scheduled.value.filter(m => m.status === 'pending').length
);
const newSchedMsg = ref('');
const newSchedAt = ref('');
const schedLabel = s =>
  ({
    pending: t('KANBAN.detail.schedStatus.pending'),
    sent: t('KANBAN.detail.schedStatus.sent'),
    failed: t('KANBAN.detail.schedStatus.failed'),
    cancelled: t('KANBAN.detail.schedStatus.cancelled'),
  })[s] || s;
const schedCls = s =>
  ({
    pending: 'text-amber-600',
    sent: 'text-n-teal-11',
    failed: 'text-red-600',
    cancelled: 'text-n-slate-10',
  })[s] || '';
const fmtDateTime = iso => (iso ? new Date(iso).toLocaleString('pt-BR') : '');
const addScheduled = async () => {
  const c = newSchedMsg.value.trim();
  if (!c || !newSchedAt.value || !conv.value) return;
  await axios.post(`${base}/scheduled_messages`, {
    conversation_id: conv.value.id,
    content: c,
    scheduled_at: new Date(newSchedAt.value).toISOString(),
  });
  newSchedMsg.value = '';
  newSchedAt.value = '';
  await load();
};
const cancelScheduled = async m => {
  await axios.delete(`${base}/scheduled_messages/${m.id}`);
  await load();
};

// Sequências (execuções de flow)
const selSeqFlow = ref(null);
const seqStatusLabel = s =>
  ({
    running: t('KANBAN.detail.seqStatus.running'),
    waiting: t('KANBAN.detail.seqStatus.waiting'),
    completed: t('KANBAN.detail.seqStatus.completed'),
    failed: t('KANBAN.detail.seqStatus.failed'),
    cancelled: t('KANBAN.detail.seqStatus.cancelled'),
    paused: t('KANBAN.detail.seqStatus.paused'),
  })[s] || s;
const seqStatusCls = s =>
  ({
    running: 'text-blue-600',
    waiting: 'text-amber-600',
    completed: 'text-n-teal-11',
    failed: 'text-red-600',
    cancelled: 'text-n-slate-10',
    paused: 'text-n-amber-11',
  })[s] || '';
const seqActive = s => ['running', 'waiting', 'waiting_response'].includes(s);
const startSequence = async () => {
  if (!selSeqFlow.value || !conv.value) return;
  await axios.post(`${base}/flow_executions`, {
    flow_id: selSeqFlow.value,
    conversation_id: conv.value.id,
  });
  selSeqFlow.value = null;
  await loadSequences();
};
const cancelSequence = async e => {
  await axios.delete(`${base}/flow_executions/${e.id}`);
  await loadSequences();
};
const pauseSequence = async e => {
  await axios.patch(`${base}/flow_executions/${e.id}/pause`);
  await loadSequences();
};
const resumeSequence = async e => {
  await axios.patch(`${base}/flow_executions/${e.id}/resume`);
  await loadSequences();
};
// Campos personalizados
const customFields = computed(() => card.value?.custom_fields || []);
const saveCf = async f => {
  let val = cfValues.value[f.id];
  if (f.field_type === 'checkbox') val = val ? 'true' : 'false';
  await axios.put(`${base}/cards/${cardId}/custom_field_values/${f.id}`, {
    value: val,
  });
};
const addTask = async () => {
  const taskTitle = newTaskTitle.value.trim();
  if (!taskTitle) return;
  await axios.post(`${base}/cards/${cardId}/tasks`, {
    title: taskTitle,
    due_date: newTaskDue.value || null,
  });
  newTaskTitle.value = '';
  newTaskDue.value = '';
  await load();
};
const toggleTask = async tk => {
  await axios.patch(`${base}/cards/${cardId}/tasks/${tk.id}`, {
    done: !tk.done,
  });
  await load();
};
const removeTask = async tk => {
  await axios.delete(`${base}/cards/${cardId}/tasks/${tk.id}`);
  await load();
};
const goBack = () => router.push(`/app/accounts/${accountId}/kanban`);
const openInChatwoot = () => {
  if (conv.value?.display_id)
    window.location.href = `/app/accounts/${accountId}/conversations/${conv.value.display_id}`;
};
const fmtDate = iso => (iso ? new Date(iso).toLocaleDateString('pt-BR') : '');

const TABS = computed(() => [
  { key: 'tarefas', label: t('KANBAN.detail.tabs.tasks') },
  { key: 'anexos', label: t('KANBAN.detail.tabs.attachments') },
  { key: 'agendadas', label: t('KANBAN.detail.tabs.scheduled') },
  { key: 'itens', label: t('KANBAN.detail.tabs.items') },
  { key: 'sequencias', label: t('KANBAN.detail.tabs.sequences') },
  { key: 'outros', label: t('KANBAN.detail.tabs.others') },
]);
const pendingModule = computed(() => ({
  outros: t('KANBAN.detail.tabs.others'),
}));

onMounted(load);
</script>

<template>
  <div
    class="flex flex-col w-full h-full overflow-y-auto bg-n-alpha-1 dark:bg-n-solid-1"
  >
    <!-- header (identidade Chatwoot, sem fundo colorido) -->
    <div
      class="sticky top-0 z-10 flex items-center gap-3 px-6 h-16 shrink-0 bg-n-background border-b border-n-weak"
    >
      <button
        class="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-n-weak text-n-slate-11 bg-n-background hover:bg-n-alpha-1"
        :title="t('KANBAN.funnelManagement.back')"
        @click="goBack"
      >
        <span class="i-lucide-chevron-left size-5" />
      </button>
      <div
        class="flex items-center justify-center w-9 h-9 rounded-lg bg-n-alpha-2 text-n-slate-11 shrink-0"
      >
        <span class="i-lucide-clipboard-list size-5" />
      </div>
      <div class="min-w-0">
        <div class="text-base font-semibold truncate text-n-slate-12">
          {{ title }}
        </div>
        <div class="text-xs text-n-slate-10 truncate">{{ subtitle }}</div>
      </div>
      <span
        v-if="card"
        class="px-2 py-0.5 ml-1 text-xs rounded-full shrink-0"
        :class="leadCls(card.lead_status)"
        >{{ leadLabel(card.lead_status) }}</span
      >
      <button
        class="ml-auto inline-flex items-center justify-center w-9 h-9 rounded-lg text-n-slate-10 hover:bg-n-alpha-1 hover:text-n-slate-12"
        :title="t('KANBAN.funnelManagement.closeFunnel')"
        @click="goBack"
      >
        <span class="i-lucide-x size-5" />
      </button>
    </div>

    <div v-if="loading" class="p-8 text-n-slate-11">
      {{ t('KANBAN.loading') }}
    </div>
    <div v-else-if="!card" class="p-8 text-n-slate-11">
      {{ t('KANBAN.detail.cardNotFound') }}
    </div>
    <div v-else class="flex flex-col max-w-4xl gap-4 p-6 mx-auto w-full">
      <!-- Conversa vinculada -->
      <div
        v-if="conv"
        class="p-4 bg-n-background border rounded-xl border-n-weak dark:bg-n-solid-2"
      >
        <div class="flex items-center justify-between mb-2">
          <h3 class="text-heading-3 text-n-slate-12">
            {{ t('KANBAN.detail.conversationNumber', { id: conv.display_id }) }}
          </h3>
          <button
            class="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-110"
            @click="openInChatwoot"
          >
            {{ t('KANBAN.detail.openInChatwoot') }}
          </button>
        </div>
        <div class="flex flex-wrap gap-x-6 gap-y-1 text-sm text-n-slate-11">
          <span
            >{{ t('KANBAN.detail.statusLabel') }}
            <b class="text-n-slate-12">{{ conv.status }}</b></span
          >
          <span
            >{{ t('KANBAN.detail.inboxLabel') }}
            <b class="text-n-slate-12">{{ conv.inbox_name }}</b></span
          >
          <span v-if="conv.assignee_name"
            >{{ t('KANBAN.detail.assigneeLabel') }}
            <b class="text-n-slate-12">{{ conv.assignee_name }}</b></span
          >
        </div>
        <div v-if="conv.labels?.length" class="flex flex-wrap gap-1 mt-2">
          <span
            v-for="l in conv.labels"
            :key="l"
            class="px-2 py-0.5 text-[0.6875rem] rounded bg-gray-100 text-gray-600 dark:bg-n-solid-3 dark:text-n-slate-11"
            >{{ l }}</span
          >
        </div>
      </div>

      <!-- Card avulso: permite vincular a uma conversa depois de criado -->
      <div
        v-else
        class="p-4 bg-n-background border rounded-xl border-n-weak dark:bg-n-solid-2"
      >
        <div class="flex items-center justify-between gap-3">
          <div class="min-w-0">
            <h3 class="text-heading-3 text-n-slate-12">
              {{ t('KANBAN.detail.linkConversation.title') }}
            </h3>
            <p class="text-sm text-n-slate-11">
              {{ t('KANBAN.detail.linkConversation.description') }}
            </p>
          </div>
          <button
            class="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-110 shrink-0"
            @click="openLinkConv"
          >
            {{ t('KANBAN.detail.linkConversation.button') }}
          </button>
        </div>
      </div>

      <!-- Modal picker de conversas p/ vincular -->
      <div
        v-if="showLinkConv"
        class="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-n-alpha-black2"
        @click.self="showLinkConv = false"
      >
        <div
          class="bg-n-background rounded-xl shadow-xl w-[90%] max-w-[460px] p-4"
        >
          <div class="flex items-center justify-between mb-3">
            <h3 class="text-heading-3 text-n-slate-12">
              {{ t('KANBAN.detail.linkConversation.title') }}
            </h3>
            <button
              class="inline-flex items-center justify-center px-1.5 text-n-slate-10 hover:text-n-slate-12"
              @click="showLinkConv = false"
            >
              <svg
                class="w-4 h-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
          <input
            v-model="linkQuery"
            :placeholder="t('KANBAN.convPicker.searchPlaceholder')"
            class="w-full px-2.5 py-1.5 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
            @keyup.enter="searchLinkConvs"
          />
          <div class="mt-3 overflow-y-auto max-h-80">
            <div v-if="linkLoading" class="p-3 text-sm text-n-slate-10">
              {{ t('KANBAN.convPicker.searching') }}
            </div>
            <div
              v-else-if="!linkResults.length"
              class="p-3 text-sm text-n-slate-10"
            >
              {{ t('KANBAN.convPicker.noConversations') }}
            </div>
            <button
              v-for="c in linkResults"
              :key="c.id"
              class="flex items-center w-full gap-2 p-2 text-left rounded-md hover:bg-n-alpha-1"
              @click="linkConversation(c)"
            >
              <span
                class="flex items-center justify-center text-xs font-medium text-white rounded-full w-7 h-7 shrink-0 bg-n-blue-9"
                >{{ (c.contact_name || '?').charAt(0).toUpperCase() }}</span
              >
              <span class="flex flex-col min-w-0"
                ><span class="text-sm truncate text-n-slate-12">{{
                  c.contact_name || t('KANBAN.card.noName')
                }}</span
                ><span class="text-xs text-n-slate-10"
                  >#{{ c.display_id }}</span
                ></span
              >
            </button>
          </div>
        </div>
      </div>

      <!-- Contato -->
      <div
        class="p-4 bg-n-background border rounded-xl border-n-weak dark:bg-n-solid-2"
      >
        <h3 class="text-heading-3 flex items-center gap-2 mb-3 text-n-slate-12">
          <svg
            class="w-4 h-4"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
          {{ t('KANBAN.detail.contact') }}
        </h3>
        <div class="space-y-2 text-sm">
          <div class="flex justify-between">
            <span class="text-n-slate-11">{{ t('KANBAN.detail.name') }}</span
            ><span class="font-medium text-n-slate-12">{{
              conv?.contact_name || card.custom_name || '-'
            }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-n-slate-11">{{ t('KANBAN.detail.phone') }}</span
            ><span class="font-medium text-n-slate-12">{{
              conv?.contact_phone || card.contact_phone || '-'
            }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-n-slate-11">{{ t('KANBAN.detail.email') }}</span
            ><span class="font-medium text-n-slate-12">{{
              conv?.contact_email || card.contact_email || '-'
            }}</span>
          </div>
        </div>
      </div>

      <!-- Campos Personalizados -->
      <div
        v-if="customFields.length"
        class="p-4 bg-n-background border rounded-xl border-n-weak dark:bg-n-solid-2"
      >
        <h3 class="text-heading-3 flex items-center gap-2 mb-3 text-n-slate-12">
          <svg
            class="w-4 h-4"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <rect x="8" y="2" width="8" height="4" rx="1" />
            <path
              d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"
            />
          </svg>
          {{ t('KANBAN.funnelManagement.customFields') }}
        </h3>
        <div class="flex flex-col gap-3">
          <div
            v-for="f in customFields"
            :key="f.id"
            class="flex flex-col gap-1"
          >
            <label class="text-xs text-n-slate-11"
              >{{ f.name
              }}<span v-if="f.required" class="text-n-ruby-11"> *</span></label
            >
            <select
              v-if="f.field_type === 'select'"
              v-model="cfValues[f.id]"
              class="pl-2 pr-8.5 py-1.5 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
              @change="saveCf(f)"
            >
              <option value="">—</option>
              <option v-for="o in f.options" :key="o" :value="o">
                {{ o }}
              </option>
            </select>
            <div
              v-else-if="f.field_type === 'multi_select'"
              class="flex flex-wrap gap-2"
            >
              <label
                v-for="o in f.options"
                :key="o"
                class="flex items-center gap-1 text-sm text-n-slate-12"
              >
                <input
                  v-model="cfValues[f.id]"
                  type="checkbox"
                  :value="o"
                  @change="saveCf(f)"
                />{{ o }}
              </label>
            </div>
            <label
              v-else-if="f.field_type === 'checkbox'"
              class="flex items-center gap-2 text-sm text-n-slate-12"
            >
              <input
                v-model="cfValues[f.id]"
                type="checkbox"
                @change="saveCf(f)"
              />
              {{ t('KANBAN.detail.yes') }}
            </label>
            <input
              v-else
              v-model="cfValues[f.id]"
              :type="
                {
                  number: 'number',
                  date: 'date',
                  email: 'email',
                  url: 'url',
                  phone: 'tel',
                }[f.field_type] || 'text'
              "
              class="px-2.5 py-1.5 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
              @change="saveCf(f)"
            />
          </div>
        </div>
      </div>

      <!-- Abas -->
      <div
        class="bg-n-background border rounded-xl border-n-weak dark:bg-n-solid-2 overflow-hidden"
      >
        <div class="flex border-b border-n-weak">
          <button
            v-for="tab in TABS"
            :key="tab.key"
            class="flex-1 py-3 px-2 text-sm font-medium text-n-slate-10 border-b-2 border-transparent hover:text-n-slate-12"
            :class="{ '!text-n-brand !border-n-brand': activeTab === tab.key }"
            @click="activeTab = tab.key"
          >
            {{ tab.label
            }}<span v-if="tab.key === 'tarefas'"> ({{ tasks.length }})</span
            ><span v-else-if="tab.key === 'itens'"> ({{ items.length }})</span
            ><span v-else-if="tab.key === 'anexos'">
              ({{ attachments.length }})</span
            ><span v-else-if="tab.key === 'agendadas'">
              ({{ pendingScheduled }})</span
            ><span v-else-if="tab.key === 'sequencias'">
              ({{
                seqExecs.filter(e => ['running', 'waiting'].includes(e.status))
                  .length
              }})</span
            >
          </button>
        </div>
        <div class="p-4">
          <!-- Tarefas (funcional) -->
          <template v-if="activeTab === 'tarefas'">
            <div class="flex items-center gap-2 mb-3">
              <input
                v-model="newTaskTitle"
                :placeholder="t('KANBAN.detail.newTaskPlaceholder')"
                class="reset-base flex-1 min-w-0 h-9 px-3 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
                @keyup.enter="addTask"
              />
              <input
                v-model="newTaskDue"
                type="date"
                class="reset-base flex-none w-[150px] h-9 px-3 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
              />
              <button
                class="inline-flex items-center justify-center flex-none w-9 h-9 rounded-lg bg-n-brand text-white hover:brightness-110 shrink-0"
                :title="t('KANBAN.detail.addTask')"
                @click="addTask"
              >
                <span class="i-lucide-plus size-5" />
              </button>
            </div>
            <div
              v-if="!tasks.length"
              class="py-6 text-sm text-center text-n-slate-10"
            >
              {{ t('KANBAN.detail.noTasks') }}
            </div>
            <div
              v-for="tk in tasks"
              :key="tk.id"
              class="flex items-center gap-2 py-2 border-b border-n-weak last:border-0"
            >
              <input
                type="checkbox"
                :checked="tk.done"
                @change="toggleTask(tk)"
              />
              <span
                class="text-sm text-n-slate-12"
                :class="{ 'line-through text-n-slate-10': tk.done }"
                >{{ tk.title }}</span
              >
              <span
                v-if="tk.due_date"
                class="inline-flex items-center gap-1 text-[0.6875rem] text-n-slate-10"
              >
                <svg
                  class="w-3 h-3"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <rect x="3" y="4" width="18" height="18" rx="2" />
                  <path d="M16 2v4M8 2v4M3 10h18" />
                </svg>
                {{ fmtDate(tk.due_date) }}</span
              >
              <button
                class="ml-auto inline-flex items-center justify-center px-1.5 text-n-ruby-11 hover:brightness-90"
                @click="removeTask(tk)"
              >
                <svg
                  class="w-4 h-4"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path
                    d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"
                  />
                </svg>
              </button>
            </div>
          </template>
          <!-- Itens (funcional) -->
          <template v-else-if="activeTab === 'itens'">
            <div
              v-if="!items.length"
              class="py-6 text-sm text-center text-n-slate-10"
            >
              {{ t('KANBAN.detail.noItems') }}
            </div>
            <div
              v-for="i in items"
              :key="i.id"
              class="flex items-center gap-2 py-2 border-b border-n-weak last:border-0"
            >
              <div class="min-w-0">
                <div class="text-sm font-medium truncate text-n-slate-12">
                  {{ i.title }}
                  <span v-if="i.quantity > 1" class="ml-1 text-n-slate-10">
                    {{ t('KANBAN.detail.quantityPrefix') }}{{ i.quantity }}
                  </span>
                </div>
                <div
                  v-if="i.description"
                  class="text-[0.6875rem] text-n-slate-10 truncate"
                >
                  {{ i.description }}
                </div>
              </div>
              <div class="ml-auto text-right">
                <div class="text-sm font-semibold text-n-teal-11">
                  {{ fmtMoney(i.value * i.quantity) }}
                </div>
                <div
                  v-if="i.quantity > 1"
                  class="text-[0.6875rem] text-n-slate-10"
                >
                  {{ fmtMoney(i.value) }} {{ t('KANBAN.detail.unitAbbr') }}
                </div>
              </div>
              <button
                class="inline-flex items-center justify-center px-1.5 text-n-ruby-11 hover:brightness-90"
                @click="removeItem(i)"
              >
                <svg
                  class="w-4 h-4"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path
                    d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"
                  />
                </svg>
              </button>
            </div>
            <div
              v-if="items.length"
              class="flex items-center justify-between pt-3 mt-2 border-t border-n-weak"
            >
              <span class="font-semibold text-n-slate-12">{{
                t('KANBAN.detail.grandTotal')
              }}</span>
              <span class="text-lg font-bold text-n-teal-11">{{
                fmtMoney(itemsTotal)
              }}</span>
            </div>
            <button
              class="mt-3 w-full px-2 py-1.5 text-xs rounded-md border border-dashed border-n-weak text-n-slate-10 bg-n-alpha-1 hover:bg-n-alpha-2"
              @click="openAddItem"
            >
              {{ t('KANBAN.detail.addItemBtn') }}
            </button>
          </template>
          <!-- Anexos (funcional) -->
          <template v-else-if="activeTab === 'anexos'">
            <input
              ref="fileInput"
              type="file"
              class="hidden"
              @change="uploadFile"
            />
            <button
              class="mb-3 w-full px-2 py-1.5 text-xs rounded-md border border-dashed border-n-weak text-n-slate-10 bg-n-alpha-1 hover:bg-n-alpha-2"
              :disabled="uploading"
              @click="pickFile"
            >
              {{
                uploading
                  ? t('KANBAN.funnelManagement.uploading')
                  : t('KANBAN.detail.addFileBtn')
              }}
            </button>
            <div
              v-if="!attachments.length"
              class="py-6 text-sm text-center text-n-slate-10"
            >
              {{ t('KANBAN.detail.noAttachments') }}
            </div>
            <div
              v-for="a in attachments"
              :key="a.id"
              class="flex items-center gap-2 py-2 border-b border-n-weak last:border-0"
            >
              <span class="text-lg">
                <svg
                  class="w-4 h-4"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path
                    d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"
                  />
                </svg>
              </span>
              <div class="min-w-0">
                <a
                  :href="a.url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-sm font-medium text-n-blue-11 truncate hover:underline"
                  >{{ a.filename }}</a
                >
                <div class="text-[0.6875rem] text-n-slate-10">
                  {{ fmtSize(a.byte_size) }}
                </div>
              </div>
              <a
                :href="a.url"
                target="_blank"
                rel="noopener noreferrer"
                class="ml-auto inline-flex items-center justify-center px-1.5 text-n-slate-10 hover:text-n-slate-12"
                :title="t('KANBAN.detail.download')"
              >
                <svg
                  class="w-4 h-4"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path d="M12 3v12M7 10l5 5 5-5M5 21h14" />
                </svg>
              </a>
              <button
                class="inline-flex items-center justify-center px-1.5 text-n-ruby-11 hover:brightness-90"
                :title="t('KANBAN.actions.delete')"
                @click="removeAttachment(a)"
              >
                <svg
                  class="w-4 h-4"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path
                    d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"
                  />
                </svg>
              </button>
            </div>
          </template>
          <!-- Agendadas (funcional) -->
          <template v-else-if="activeTab === 'agendadas'">
            <div v-if="!conv" class="py-6 text-sm text-center text-n-slate-10">
              {{ t('KANBAN.detail.scheduleRequiresConv') }}
            </div>
            <template v-else>
              <div
                class="flex flex-col gap-2.5 p-3 mb-3 rounded-lg border border-n-weak bg-n-alpha-1"
              >
                <textarea
                  v-model="newSchedMsg"
                  rows="3"
                  :placeholder="t('KANBAN.detail.messagePlaceholder')"
                  class="reset-base w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
                />
                <div class="flex items-center gap-2">
                  <input
                    v-model="newSchedAt"
                    type="datetime-local"
                    class="reset-base flex-1 min-w-0 h-9 px-3 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
                  />
                  <button
                    class="inline-flex items-center gap-1.5 h-9 px-3.5 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-110 shrink-0"
                    @click="addScheduled"
                  >
                    <span class="i-lucide-clock size-4" />
                    {{ t('KANBAN.detail.schedule') }}
                  </button>
                </div>
              </div>
              <div
                v-if="!scheduled.length"
                class="py-6 text-sm text-center text-n-slate-10"
              >
                {{ t('KANBAN.detail.noScheduled') }}
              </div>
              <div
                v-for="m in scheduled"
                :key="m.id"
                class="flex items-start gap-2 py-2 border-b border-n-weak last:border-0"
              >
                <div class="min-w-0">
                  <div class="text-sm text-n-slate-12">{{ m.content }}</div>
                  <div class="text-[0.6875rem] text-n-slate-10">
                    <svg
                      class="inline w-3 h-3"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <rect x="3" y="4" width="18" height="18" rx="2" />
                      <path d="M16 2v4M8 2v4M3 10h18" />
                    </svg>
                    {{ fmtDateTime(m.scheduled_at) }} ·
                    <span :class="schedCls(m.status)">{{
                      schedLabel(m.status)
                    }}</span>
                  </div>
                </div>
                <button
                  v-if="m.status === 'pending'"
                  class="ml-auto inline-flex items-center justify-center px-1.5 text-n-ruby-11 hover:brightness-90"
                  :title="t('KANBAN.funnelManagement.cancel')"
                  @click="cancelScheduled(m)"
                >
                  <svg
                    class="w-4 h-4"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path d="M18 6 6 18M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </template>
          </template>
          <!-- Sequências (funcional) -->
          <template v-else-if="activeTab === 'sequencias'">
            <div v-if="!conv" class="py-6 text-sm text-center text-n-slate-10">
              {{ t('KANBAN.detail.seqRequiresConv') }}
            </div>
            <template v-else>
              <div class="flex items-center gap-2 mb-2">
                <select
                  v-model="selSeqFlow"
                  class="grow h-9 pl-3 pr-8 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
                >
                  <option :value="null">
                    {{ t('KANBAN.detail.selectSequence') }}
                  </option>
                  <option v-for="f in seqFlows" :key="f.id" :value="f.id">
                    {{ f.name }}
                  </option>
                </select>
                <button
                  class="inline-flex items-center gap-1.5 h-9 px-3.5 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-110 disabled:opacity-60 shrink-0"
                  :disabled="!selSeqFlow"
                  @click="startSequence"
                >
                  {{ t('KANBAN.detail.start') }}
                </button>
              </div>
              <div
                v-if="!seqFlows.length"
                class="mb-2 text-[0.6875rem] text-n-slate-10"
              >
                {{ t('KANBAN.detail.noActiveSequences') }}
              </div>
              <div
                v-if="!seqExecs.length"
                class="py-6 text-sm text-center text-n-slate-10"
              >
                {{ t('KANBAN.detail.noSequencesStarted') }}
              </div>
              <div
                v-for="e in seqExecs"
                :key="e.id"
                class="flex items-center gap-2 py-2 border-b border-n-weak last:border-0"
              >
                <div class="min-w-0">
                  <div class="text-sm font-medium text-n-slate-12">
                    {{ e.flow_name }}
                  </div>
                  <div class="text-[0.6875rem]">
                    <span :class="seqStatusCls(e.status)">{{
                      seqStatusLabel(e.status)
                    }}</span
                    ><span v-if="e.next_run_at" class="text-n-slate-10">
                      {{
                        t('KANBAN.detail.nextRun', {
                          time: fmtDateTime(e.next_run_at),
                        })
                      }}</span
                    >
                  </div>
                </div>
                <div class="flex items-center gap-1.5 ml-auto">
                  <button
                    v-if="seqActive(e.status)"
                    class="inline-flex items-center gap-1 px-2 py-1 text-[0.6875rem] font-medium rounded-md border border-n-weak text-n-amber-11 hover:bg-n-alpha-1"
                    @click="pauseSequence(e)"
                  >
                    {{ t('CHATBOT.flowBox.pause') }}
                  </button>
                  <button
                    v-if="e.status === 'paused'"
                    class="inline-flex items-center gap-1 px-2 py-1 text-[0.6875rem] font-medium rounded-md border border-n-weak text-n-teal-11 hover:bg-n-alpha-1"
                    @click="resumeSequence(e)"
                  >
                    {{ t('CHATBOT.flowBox.resume') }}
                  </button>
                  <button
                    v-if="seqActive(e.status) || e.status === 'paused'"
                    class="inline-flex items-center justify-center px-1.5 text-n-ruby-11 hover:brightness-90"
                    :title="t('CHATBOT.flowBox.remove')"
                    @click="cancelSequence(e)"
                  >
                    <svg
                      class="w-4 h-4"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path d="M18 6 6 18M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            </template>
          </template>
          <!-- demais abas: placeholder do módulo -->
          <div v-else class="py-8 text-sm text-center text-n-slate-10">
            {{
              t('KANBAN.detail.moduleMigrating', {
                module: pendingModule[activeTab],
              })
            }}
          </div>
        </div>
      </div>
    </div>

    <!-- Modal: adicionar item -->
    <div
      v-if="showAddItem"
      class="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-n-alpha-black2"
      @click.self="showAddItem = false"
    >
      <div
        class="bg-n-background rounded-xl shadow-xl w-[90%] max-w-[460px] p-4"
      >
        <div class="flex items-center justify-between mb-3">
          <h3 class="text-heading-3 text-n-slate-12">
            {{ t('KANBAN.detail.addItemTitle') }}
          </h3>
          <button
            class="inline-flex items-center justify-center px-1.5 text-n-slate-10 hover:text-n-slate-12"
            @click="showAddItem = false"
          >
            <svg
              class="w-4 h-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div
          v-if="templates.length"
          class="flex gap-1 p-1 mb-3 rounded-lg bg-n-alpha-1 dark:bg-n-solid-3"
        >
          <button
            class="flex-1 px-3 py-1.5 text-sm font-medium rounded-md text-n-slate-10"
            :class="{
              'bg-n-background text-n-slate-12 shadow-sm':
                addMode === 'template',
            }"
            @click="addMode = 'template'"
          >
            {{ t('KANBAN.detail.tabTemplate') }}
          </button>
          <button
            class="flex-1 px-3 py-1.5 text-sm font-medium rounded-md text-n-slate-10"
            :class="{
              'bg-n-background text-n-slate-12 shadow-sm': addMode === 'manual',
            }"
            @click="addMode = 'manual'"
          >
            {{ t('KANBAN.detail.tabManual') }}
          </button>
        </div>
        <div
          v-if="addMode === 'template' && templates.length"
          class="flex flex-col gap-1 mb-3 overflow-y-auto max-h-48"
        >
          <button
            v-for="tpl in templates"
            :key="tpl.id"
            class="flex items-center gap-2 px-2.5 py-2 rounded-md border border-n-weak bg-n-background hover:bg-n-alpha-1"
            :class="{
              '!border-n-brand bg-n-alpha-1': selTemplate?.id === tpl.id,
            }"
            @click="pickTemplate(tpl)"
          >
            <span class="text-sm text-n-slate-12">{{ tpl.title }}</span>
            <span class="ml-auto text-sm font-medium text-n-teal-11">{{
              fmtMoney(tpl.value)
            }}</span>
          </button>
        </div>
        <div class="flex flex-col gap-2">
          <input
            v-model="itForm.title"
            :placeholder="t('KANBAN.detail.itemTitlePlaceholder')"
            class="w-full px-2.5 py-1.5 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
          />
          <input
            v-model="itForm.description"
            :placeholder="t('KANBAN.detail.itemDescPlaceholder')"
            class="w-full px-2.5 py-1.5 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
          />
          <div class="flex gap-2">
            <label class="flex-1 text-xs text-n-slate-11"
              >{{ t('KANBAN.funnelManagement.valueLabel')
              }}<input
                v-model="itForm.valueReais"
                class="w-full mt-1 px-2.5 py-1.5 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
                :placeholder="t('KANBAN.funnelManagement.valuePlaceholder')"
            /></label>
            <label class="w-24 text-xs text-n-slate-11"
              >{{ t('KANBAN.detail.quantity')
              }}<input
                v-model.number="itForm.quantity"
                type="number"
                min="1"
                class="w-full mt-1 px-2.5 py-1.5 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
            /></label>
          </div>
          <div class="text-sm text-right text-n-slate-11">
            {{ t('KANBAN.detail.subtotal') }}
            <b class="text-n-teal-11">{{
              fmtMoney(
                reaisToCents(itForm.valueReais) * (Number(itForm.quantity) || 1)
              )
            }}</b>
          </div>
        </div>
        <div class="flex gap-2 mt-3">
          <button
            class="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-110"
            @click="saveItem"
          >
            {{ t('KANBAN.funnelManagement.add') }}</button
          ><button
            class="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-n-weak text-n-slate-11 bg-n-background hover:bg-n-alpha-1"
            @click="showAddItem = false"
          >
            {{ t('KANBAN.funnelManagement.cancel') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
